/**
 * Service Descriptions - Canonical Source of Truth (v1.0)
 * 
 * All service content for cards and modals across Landing Page, Services Page, 
 * About Page, and User Portal is defined here. 
 * 
 * IMPORTANT: This content must match the backend services_catalog.py exactly.
 * Do not paraphrase or modify. This is canonical copy.
 */

export const SERVICE_IDS = {
  SERVICE_A: "SERVICE_A",
  SERVICE_B: "SERVICE_B",
  SERVICE_C: "SERVICE_C"
};

// ==============================================================================
// CANONICAL SERVICE DEFINITIONS
// ==============================================================================
// IMPORTANT: Service names MUST match Admin Pricing Configuration exactly.
// These exact strings are the CONTRACT - no aliases, no alternate labels.
// 
// Canonical Matrix:
// - SERVICE_A = "Discovery & Viability Assessment" (MID-TIER) @ $4,950
// - SERVICE_B = "A.I. Readiness Consultation" (ENTRY LEVEL) @ $1,995
// - SERVICE_C = "Strategic A.I. Roadmap & Implementation Planning" (FULL SCOPE) @ $7,950
// ==============================================================================

export const serviceDescriptions = {
  SERVICE_A: {
    id: SERVICE_IDS.SERVICE_A,
    slug: "discovery-viability-assessment",
    serviceCode: "A",
    title: "Discovery & Viability Assessment",
    tierKey: "MID_TIER",
    displayOrder: 2,
    
    // Card-level copy
    headline: "Clarity Before Commitment.",
    shortDescription: "A structured assessment to clarify opportunities, identify risks, and develop a clear picture of what A.I. adoption could—and should—look like for your specific business.",
    decisionAnchor: "Best when you need clarity before committing to any AI-related decisions.",
    
    // Pricing (gated - only visible post-evaluation + SER)
    fixedPriceCents: 495000,
    pricing: {
      adminPriceKey: "standard",
      fallbackAmountCents: 495000,
      displayLabelWhenVisible: "Price available after loading",
      displayLabelWhenHidden: "Revealed after your Evaluation Report",
      visibilityRule: "POST_EVALUATION_AND_SER"
    },
    
    // What this service does
    whatThisServiceDoes: [
      "The Discovery & Viability Assessment provides a structured, interview-based assessment of your current business operations, decision environment, and readiness to engage with artificial intelligence.",
      "This service is designed to replace assumptions with evidence and help leaders understand their real operating context before committing time, money, or credibility to AI initiatives."
    ],
    
    // Deliverables
    deliverables: [
      "Two structured leadership interviews (recorded)",
      "Current-state operational analysis",
      "AI readiness and risk indicators",
      "Organizational and process complexity mapping",
      "Decision pressure vs. readiness assessment",
      "Discovery & Viability Assessment Report (PDF)"
    ],
    
    // Risk prevention (shows on card)
    whatThisPrevents: [
      "Acting on assumptions instead of operational reality",
      "Premature AI decisions based on hype or incomplete information",
      "Misalignment between leadership perception and business conditions",
      "Wasted time debating solutions before problems are understood"
    ],
    
    // Scope boundaries (mandatory on card and modal)
    doesNotInclude: [
      "Tool or vendor recommendations",
      "Implementation planning or technical specifications",
      "Ongoing advisory or follow-up sessions",
      "Guarantees of ROI, outcomes, or transformation"
    ],
    
    // Important clarification (null for Service A)
    importantClarification: null,
    
    // Modal sections (verbatim, canonical)
    modalSections: [
      {
        id: "purpose",
        heading: "Purpose of This Service",
        body: [
          "The Discovery & Viability Assessment exists to establish situational clarity before any AI-related commitments are made.",
          "Many organizations feel pressure to \"do something with AI\" before fully understanding their own operating reality. This service slows the process down intelligently by replacing assumptions with evidence."
        ]
      },
      {
        id: "delivers",
        heading: "What This Service Actually Delivers",
        body: [
          "This engagement produces a structured, interview-based assessment of how your business operates today, how AI-related pressure is being applied, and whether your organization is realistically prepared to engage with artificial intelligence.",
          "The outcome is not a recommendation to adopt AI. The outcome is clarity about where you stand."
        ]
      },
      {
        id: "evidence",
        heading: "Evidence Used",
        body: [
          "Two recorded leadership interviews",
          "Submitted onboarding and evaluation inputs",
          "Operational signals derived from interview transcripts",
          "All conclusions are grounded in the evidence collected for this service."
        ]
      },
      {
        id: "deliverable",
        heading: "Final Deliverable",
        body: [
          "You will receive the Discovery & Viability Assessment Report, an executive-ready PDF that includes:",
          "• A clear description of your current operating context",
          "• AI readiness and risk indicators",
          "• Organizational and process complexity mapping",
          "• Decision pressure vs. readiness assessment",
          "• Explicit assumptions and confidence disclosures (if required)",
          "This report is designed to be shared internally and used as a decision-support artifact."
        ]
      },
      {
        id: "not_do",
        heading: "What This Service Does NOT Do",
        body: [
          "This service does not recommend tools or vendors, design systems or workflows, plan or manage implementation, provide ongoing advisory services, or guarantee outcomes, ROI, or transformation."
        ]
      },
      {
        id: "when",
        heading: "When This Service Makes Sense",
        body: [
          "Choose the Discovery & Viability Assessment if you need clarity before committing resources, approving initiatives, or engaging vendors."
        ]
      }
    ]
  },

  SERVICE_B: {
    id: SERVICE_IDS.SERVICE_B,
    slug: "ai-readiness-consultation",
    serviceCode: "B",
    title: "A.I. Readiness Consultation",
    tierKey: "ENTRY_LEVEL",
    displayOrder: 1,
    
    // Card-level copy
    headline: "Start with Clarity.",
    shortDescription: "A focused conversation to address your specific questions, validate your current thinking, and determine whether further exploration makes sense for your business.",
    decisionAnchor: "Best when you need expert guidance before making any AI-related commitments.",
    
    // Pricing (gated - only visible post-evaluation + SER)
    fixedPriceCents: 199500,
    pricing: {
      adminPriceKey: "foundation",
      fallbackAmountCents: 199500,
      displayLabelWhenVisible: "Price available after loading",
      displayLabelWhenHidden: "Revealed after your Evaluation Report",
      visibilityRule: "POST_EVALUATION_AND_SER"
    },
    
    // What this service does
    whatThisServiceDoes: [
      "The A.I. Readiness Consultation provides a focused, expert-led conversation to help you understand your current position relative to AI adoption.",
      "This service exists to validate your thinking, address specific questions, and determine whether deeper exploration is warranted for your business."
    ],
    
    // Deliverables
    deliverables: [
      "One-on-one consultation session (recorded)",
      "Current AI readiness assessment",
      "Key questions and concerns addressed",
      "Initial opportunity identification",
      "Recommended next steps",
      "A.I. Readiness Consultation Summary (PDF)"
    ],
    
    // Risk prevention
    whatThisPrevents: [
      "Making uninformed AI decisions without expert input",
      "Wasting time on initiatives that don't fit your situation",
      "Falling for vendor hype without objective perspective",
      "Paralysis from information overload"
    ],
    
    // Scope boundaries
    doesNotInclude: [
      "Detailed operational analysis",
      "Multi-stakeholder interviews",
      "Implementation planning or execution",
      "Technical integration or development work",
      "Guarantees of ROI or outcomes"
    ],
    
    // Important clarification (Service B has this)
    importantClarification: [
      "This service is delivered as a complete, stand-alone engagement.",
      "If prior A.I. Means Business context exists, it may be referenced to improve analytical precision, but no other services are bundled or inherited."
    ],
    
    // Modal sections
    modalSections: [
      {
        id: "purpose",
        heading: "Purpose of This Service",
        body: [
          "The A.I. Readiness Consultation exists to provide expert guidance before any AI-related commitments are made.",
          "This service is designed for organizations that need a focused conversation to validate their thinking and determine whether further exploration makes sense."
        ]
      },
      {
        id: "delivers",
        heading: "What This Service Actually Delivers",
        body: [
          "This engagement provides a one-on-one consultation session to understand your current position relative to AI adoption.",
          "The goal is to address your specific questions, validate your current thinking, and provide clarity on next steps."
        ]
      },
      {
        id: "evidence",
        heading: "Evidence Used",
        body: [
          "One recorded consultation session",
          "Your evaluation inputs and context",
          "Your specific questions and concerns",
          "All conclusions are grounded in the information collected during this consultation."
        ]
      },
      {
        id: "deliverable",
        heading: "Final Deliverable",
        body: [
          "You will receive the A.I. Readiness Consultation Summary, an executive-ready PDF that includes:",
          "• Current AI readiness assessment",
          "• Key questions and concerns addressed",
          "• Initial opportunity identification",
          "• Recommended next steps",
          "• Expert guidance tailored to your situation",
          "This summary is designed to support your decision-making process."
        ]
      },
      {
        id: "clarification",
        heading: "Important Clarification",
        body: [
          "This service is delivered as a complete, stand-alone engagement.",
          "If prior A.I. Means Business context exists, it may be referenced to improve analytical precision. No other services are bundled, inherited, or discounted."
        ]
      },
      {
        id: "not_do",
        heading: "What This Service Does NOT Do",
        body: [
          "This service does not provide detailed operational analysis, multi-stakeholder interviews, implementation planning or execution, technical integration or development, or provide guarantees of ROI or outcomes."
        ]
      },
      {
        id: "when",
        heading: "When This Service Makes Sense",
        body: [
          "Choose the A.I. Readiness Consultation if you need expert guidance and want to validate your thinking before committing to a larger engagement."
        ]
      }
    ]
  },

  SERVICE_C: {
    id: SERVICE_IDS.SERVICE_C,
    slug: "strategic-ai-roadmap",
    serviceCode: "C",
    title: "Strategic A.I. Roadmap & Implementation Planning",
    tierKey: "FULL_SCOPE",
    displayOrder: 3,
    
    // Card-level copy
    headline: "Plan Responsibly. Decide Deliberately.",
    shortDescription: "A comprehensive engagement for organizations facing significant complexity or urgency. Includes detailed operational analysis, prioritized implementation roadmap, vendor-neutral technology assessment, and ongoing advisory support.",
    decisionAnchor: "Best when you need a defensible plan to sequence AI-related decisions over time.",
    
    // Pricing (gated - only visible post-evaluation + SER)
    fixedPriceCents: 795000,
    pricing: {
      adminPriceKey: "comprehensive",
      fallbackAmountCents: 795000,
      displayLabelWhenVisible: "Price available after loading",
      displayLabelWhenHidden: "Revealed after your Evaluation Report",
      visibilityRule: "POST_EVALUATION_AND_SER"
    },
    
    // What this service does
    whatThisServiceDoes: [
      "The Strategic A.I. Roadmap & Implementation Planning provides a structured, phased approach to planning and prioritizing AI-related decisions based on your goals, constraints, and risk tolerance.",
      "This service delivers a board-ready decision artifact designed to help leaders sequence decisions responsibly—without committing to execution."
    ],
    
    // Deliverables
    deliverables: [
      "Extended leadership and stakeholder interviews (recorded)",
      "Phased AI decision roadmap with entry and exit criteria",
      "Risk sequencing and prioritization framework",
      "Capability vs. dependency analysis",
      "Cost–benefit scenario ranges (non-guaranteed)",
      "Strategic A.I. Roadmap & Implementation Planning Report (PDF)"
    ],
    
    // Risk prevention
    whatThisPrevents: [
      "Scaling fragmented or ad-hoc AI efforts into systemic complexity",
      "Locking into technical paths that are costly to unwind",
      "Leadership disagreement caused by unclear priorities",
      "Long-term initiatives driven by momentum instead of evidence"
    ],
    
    // Scope boundaries
    doesNotInclude: [
      "Vendor negotiations, RFPs, or procurement support",
      "Implementation project management",
      "Technical architecture or system design",
      "Ongoing retainers or advisory beyond the defined scope",
      "Guarantees of competitive advantage or market outcomes"
    ],
    
    // Important clarification (Service C has this)
    importantClarification: [
      "This service does not bundle or include other A.I. Means Business services.",
      "It delivers a distinct, stand-alone strategic decision artifact focused on sequencing, governance, and risk."
    ],
    
    // Modal sections
    modalSections: [
      {
        id: "purpose",
        heading: "Purpose of This Service",
        body: [
          "The Strategic A.I. Roadmap & Implementation Planning exists to help leaders sequence AI-related decisions responsibly over time.",
          "AI decisions are difficult to reverse. This service is designed to help organizations plan deliberately rather than reactively."
        ]
      },
      {
        id: "delivers",
        heading: "What This Service Actually Delivers",
        body: [
          "This engagement produces a structured, phased decision framework based on your organization's goals, constraints, and risk tolerance.",
          "It does not produce an execution plan. It produces a defensible decision roadmap."
        ]
      },
      {
        id: "evidence",
        heading: "Evidence Used",
        body: [
          "Extended leadership and stakeholder interviews",
          "Strategic and operational inputs",
          "Constraint, dependency, and risk signals validated across participants",
          "All required interviews and transcripts must be completed before synthesis begins."
        ]
      },
      {
        id: "deliverable",
        heading: "Final Deliverable",
        body: [
          "You will receive the Strategic A.I. Roadmap & Implementation Planning Report, a board-ready PDF that includes:",
          "• A phased AI decision roadmap with entry and exit criteria",
          "• Risk sequencing and prioritization framework",
          "• Capability vs. dependency analysis",
          "• Cost–benefit scenario ranges (non-guaranteed)",
          "• Explicit assumptions, tradeoffs, and decision implications",
          "This artifact is designed to support executive and board-level discussions."
        ]
      },
      {
        id: "clarification",
        heading: "Important Clarification",
        body: [
          "This service does not bundle or include other A.I. Means Business services.",
          "It delivers a distinct, stand-alone strategic decision artifact focused on sequencing, governance, and risk."
        ]
      },
      {
        id: "not_do",
        heading: "What This Service Does NOT Do",
        body: [
          "This service does not negotiate vendors or issue RFPs, manage implementation projects, design technical architectures, provide ongoing retainers beyond defined scope, or guarantee competitive advantage or market outcomes."
        ]
      },
      {
        id: "when",
        heading: "When This Service Makes Sense",
        body: [
          "Choose the Strategic A.I. Roadmap & Implementation Planning if you need a defensible plan to guide AI-related decisions over time—without committing to execution."
        ]
      }
    ]
  }
};

// Array version for iteration (sorted by displayOrder)
export const serviceList = Object.values(serviceDescriptions).sort(
  (a, b) => a.displayOrder - b.displayOrder
);

// Get service by ID
export const getServiceById = (serviceId) => serviceDescriptions[serviceId] || null;

// Footer disclaimer (non-removable)
export const SERVICE_DISCLAIMER = "A.I. Means Business provides analysis and decision-support only. We do not guarantee outcomes.";

// Format price helper
export const formatPriceCents = (priceCents) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(priceCents / 100);
};
